import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getCookie, QaToolKitOptions } from '@cashier/cashier-common';
import { FormStatuses, IntegrationThemes } from '@cashier/cashier-model';
import { cn, Loader } from '@cashier/cashier-ui';

import { AmountValueProvider } from '../contexts';
import {
  useFormAction,
  useFormData,
  useFormStatus,
  usePaymentMethod,
} from '../hooks';
import { AgreementCheckbox } from '../modules/agreementCheckbox';
import { AmountField } from '../modules/amountField/v2';
import { SecuredCardData, StandardCardData } from '../modules/cardData';
import { Description } from '../modules/description';
import { ErrorList } from '../modules/errorList/v2';
import { FieldWrapper } from '../modules/fieldWrapper/v2';
import { PaymentMethodButton } from '../modules/paymentMethodButton';
import { SubmitButton } from '../modules/submitButton/v2';

import styles from './styles.module.scss';

export const PaymentForm = (): ReactElement => {
  const { t, ready: translationsReady } = useTranslation();

  const status = useFormStatus();
  const { onFormSubmitted } = useFormAction();
  const { schema } = useFormData();
  const paymentMethod = usePaymentMethod();

  const isHostedFieldEnabled =
    getCookie(QaToolKitOptions.enableHostedField) === 'true';

  const { cardData, staticData, commonFieldMap } = schema || {};

  return (
    <div>
      <div
        className={cn({
          [styles.section]: true,
          [styles.sectionWrapperByPlatform]: true,
        })}
      >
        {status === FormStatuses.INITIATED && (
          <div className={cn([styles.sectionContent, styles.loader])}>
            <div className={styles.loaderContainer}>
              <Loader size="medium" theme="colored" />
            </div>
            <div className={styles.loaderText}>{t('Loading')}</div>
          </div>
        )}

        {status !== FormStatuses.INITIATED && (
          <div
            className={cn({
              [styles.sectionContent]: true,
              [styles.sectionByPlatform]: true,
            })}
          >
            <AmountValueProvider
              initialValue={
                staticData?.amount ? staticData?.amount.value : null
              }
            >
              <div
                className={cn({
                  [styles.form]: true,
                  [styles.empty]: !schema,
                  [styles.withoutMargin]: true,
                  [styles.bottomMargin]: true,
                })}
              >
                <div
                  className={cn({
                    [styles.formField]: true,
                    [styles.emptyBottom]: !schema,
                  })}
                >
                  <div
                    className={cn({
                      [styles.row]: true,
                      [styles.emptyTop]: !schema,
                      [styles.withoutMargin]: true,
                    })}
                  >
                    <div
                      className={cn({
                        [styles.col]: true,
                        [styles.full]: true,
                      })}
                    >
                      <div className={styles.container}>
                        <PaymentMethodButton
                          name={paymentMethod.name}
                          logoSrc={paymentMethod.logo}
                        />
                        {staticData?.amount && (
                          <AmountField
                            field={staticData.amount}
                            version={'v2'}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={styles.error}>
                    <ErrorList />
                  </div>

                  {paymentMethod.description && (
                    <Description content={paymentMethod.description} />
                  )}

                  {commonFieldMap &&
                    Object.entries(commonFieldMap).map(([key, field]) => (
                      <FieldWrapper key={key} field={field} version={'v2'} />
                    ))}

                  {cardData && (
                    <>
                      {isHostedFieldEnabled ? (
                        <SecuredCardData schema={cardData} />
                      ) : (
                        <StandardCardData schema={cardData} version={'v2'} />
                      )}
                    </>
                  )}
                </div>
              </div>

              {cardData?.termsAgreementToggler && translationsReady && (
                <div
                  className={cn({
                    [styles.checkboxParent]: true,
                  })}
                >
                  <AgreementCheckbox
                    field={cardData.termsAgreementToggler}
                    theme={IntegrationThemes.Platform}
                    version={'v2'}
                  />
                </div>
              )}
              <SubmitButton onClick={onFormSubmitted} />
            </AmountValueProvider>
          </div>
        )}
      </div>
    </div>
  );
};
